import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { SERVER_URL } from '../app/ServerConfig';


// TypeScript prop types for all custom components
interface ContainerProps {
  isMobile: boolean;
  children: React.ReactNode;
}
const Container: React.FC<ContainerProps> = ({ isMobile, children }) => (
  <div
    style={{
      maxWidth: 500,
      margin: isMobile ? '20px 12px' : '40px auto',
      padding: isMobile ? 20 : 32,
      border: '1px solid #e1e5e9',
      borderRadius: 12,
      background: '#fff',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}
  >
    {children}
  </div>
);

interface TitleProps {
  isMobile: boolean;
  children: React.ReactNode;
}
const Title: React.FC<TitleProps> = ({ isMobile, children }) => (
  <h2 style={{ 
    fontSize: isMobile ? 20 : 24, 
    textAlign: 'center', 
    marginBottom: 24, 
    fontWeight: 600,
    color: '#1a365d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8
  }}>
    <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
    {children}
  </h2>
);

interface StatusPillProps {
  status: 'expired' | 'loading' | 'valid';
}
const StatusPill: React.FC<StatusPillProps> = ({ status }) => {
  const statusConfig = {
    expired: { color: '#e53e3e', bg: '#fed7d7', text: 'Link Expired' },
    loading: { color: '#d69e2e', bg: '#fefcbf', text: 'Loading...' },
    valid: { color: '#38a169', bg: '#c6f6d5', text: 'Link Valid' }
  };
  const config = statusConfig[status] || statusConfig.expired;
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '6px 12px',
      borderRadius: 20,
      backgroundColor: config.bg,
      color: config.color,
      fontSize: 12,
      fontWeight: 500,
      marginBottom: 20
    }}>
      <div style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: config.color,
        marginRight: 6
      }}></div>
      {config.text}
    </div>
  );
};




interface UploadAreaProps {
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  uploading: boolean;
  maxReached: boolean;
  isMobile: boolean;
}
const UploadArea: React.FC<UploadAreaProps> = ({ onUpload, uploading, maxReached, isMobile }) => {
  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && !maxReached) {
      onUpload({ target: { files: e.dataTransfer.files } } as any);
    }
  };
  return (
    <div 
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${isDragging ? '#4299e1' : maxReached ? '#cbd5e0' : '#cbd5e0'}`,
        borderRadius: 8,
        padding: isMobile ? 20 : 24,
        textAlign: 'center',
        backgroundColor: isDragging ? '#ebf8ff' : maxReached ? '#f7fafc' : '#f7fafc',
        transition: 'all 0.2s',
        marginBottom: 16,
        opacity: maxReached ? 0.6 : 1
      }}
    >
      <div style={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        backgroundColor: maxReached ? '#e2e8f0' : '#ebf8ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
        color: maxReached ? '#a0aec0' : '#4299e1'
      }}>
        {uploading ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
            <line x1="12" y1="2" x2="12" y2="6"></line>
            <line x1="12" y1="18" x2="12" y2="22"></line>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
            <line x1="2" y1="12" x2="6" y2="12"></line>
            <line x1="18" y1="12" x2="22" y2="12"></line>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
        )}
      </div>
      <div style={{ 
        fontSize: 16, 
        fontWeight: 500, 
        color: maxReached ? '#a0aec0' : '#2d3748',
        marginBottom: 8 
      }}>
        {maxReached ? 'Maximum documents uploaded' : 'Upload Document'}
      </div>
      <div style={{ 
        fontSize: 14, 
        color: '#718096', 
        marginBottom: 16,
        lineHeight: 1.5
      }}>
        {maxReached 
          ? 'You have reached the maximum of 2 documents. Remove existing files to upload new ones.' 
          : 'Drag & drop your PDF or image file here (JPG, PNG) or click to browse'}
      </div>
      <div>
        <label style={{
          display: 'inline-block',
          padding: '10px 20px',
          backgroundColor: maxReached ? '#e2e8f0' : '#4299e1',
          color: maxReached ? '#a0aec0' : 'white',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 500,
          cursor: maxReached ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          opacity: uploading ? 0.7 : 1
        }}>
          {uploading ? 'Uploading...' : 'Select PDF/Image File'}
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/jpg"
            disabled={uploading || maxReached}
            onChange={onUpload}
            style={{ display: 'none' }}
          />
        </label>
      </div>
      <div style={{ 
        fontSize: 12, 
        color: '#a0aec0', 
        marginTop: 12 
      }}>
        Only PDF or image files (JPG, PNG, PDF) accepted (Max: 10MB)
      </div>
    </div>
  );
};

interface ErrorMessageProps {
  error: string | null;
  onDismiss: () => void;
}
const ErrorMessage: React.FC<ErrorMessageProps> = ({ error, onDismiss }) => (
  error ? (
    <div style={{
      padding: 12,
      backgroundColor: '#fed7d7',
      color: '#c53030',
      borderRadius: 6,
      marginBottom: 16,
      display: 'flex',
      alignItems: 'flex-start',
      fontSize: 14
    }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0, marginTop: 1 }}>
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <div style={{ flex: 1 }}>{error}</div>
      <button 
        onClick={onDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: '#c53030',
          cursor: 'pointer',
          marginLeft: 8,
          padding: 0,
          display: 'flex'
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  ) : null
);

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 600);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

interface UploadedDoc {
  fileName: string;
  uploadedAt: string;
}

const TransporterUploadPage: React.FC = () => {
  const location = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [docs, setDocs] = useState<UploadedDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    setToken(t);
    if (t) {
      validateToken(t);
    } else {
      setExpired(true);
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [location.search]);


  const validateToken = async (t: string) => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await axios.get(`${SERVER_URL}/logistics-upload/validate-link?token=${t}`);
      if (res.data?.responseStatus === 400 && res.data?.responseMessage) {
        setApiError(res.data.responseMessage);
        setExpired(true);
      } else if (res.data?.expired) {
        setExpired(true);
      } else {
        setExpired(false);
        setDocs(res.data?.documents || []);
      }
    } catch (e: any) {
      setExpired(true);
    }
    setLoading(false);
  };



  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!token || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF or image files (JPG, PNG) are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('token', token);
    try {
      await axios.post(`${SERVER_URL}/logistics-upload/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadSuccess(true);
    } catch (e) {
      setError('Upload failed. Please try again.');
    }
    setUploading(false);
  };

  return (
    <Container isMobile={isMobile}>
      <Title isMobile={isMobile}>Transporter Document Upload</Title>
      
      <div style={{ textAlign: 'center' }}>
        {apiError && (
          <div style={{ color: '#e53e3e', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, padding: 12, marginBottom: 12, fontWeight: 500 }}>
            {apiError}
          </div>
        )}
        {loading ? (
          <StatusPill status="loading" />
        ) : expired ? (
          <StatusPill status="expired" />
        ) : (
          <StatusPill status="valid" />
        )}
      </div>
      
  {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 0',
          color: '#718096'
        }}>
          <div style={{ 
            width: 48, 
            height: 48, 
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #4299e1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          Validating your upload link...
        </div>
      ) : expired ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px 0',
          color: '#718096'
        }}>
          <div style={{ 
            width: 64, 
            height: 64, 
            borderRadius: '50%', 
            backgroundColor: '#fed7d7', 
            color: '#c53030',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h3 style={{ color: '#2d3748', marginBottom: 8 }}>Link Expired or Invalid</h3>
          <p style={{ lineHeight: 1.5 }}>
            This upload link has expired or is invalid. Please request a new upload link from your transporter.
          </p>
        </div>
      ) : (
        uploadSuccess ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 220
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#c6f6d5', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" stroke="#38a169" strokeWidth="2" fill="#c6f6d5" />
                <path d="M9 12l2 2l4-4" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
            <div style={{ fontWeight: 600, fontSize: 20, color: '#2f855a', marginBottom: 8 }}>Thanks for uploading!</div>
            <div style={{ fontSize: 15, color: '#4a5568', marginBottom: 18, textAlign: 'center' }}>
              Your document has been uploaded successfully.
            </div>
          </div>
        ) : (
          <>
            <ErrorMessage error={error} onDismiss={() => setError(null)} />
            <UploadArea 
              onUpload={handleUpload} 
              uploading={uploading} 
              maxReached={docs.length >= 2}
              isMobile={isMobile}
            />
            <div style={{ 
              fontSize: 13, 
              color: '#718096', 
              lineHeight: 1.5,
              padding: 12,
              backgroundColor: '#f8fafc',
              borderRadius: 6
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0, marginTop: 2 }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <span>You can upload up to 2 documents. Each file must be a PDF or image (JPG, PNG) under 10MB.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0, marginTop: 2 }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <span>These documents will be securely delivered to your transporter.</span>
              </div>
            </div>
          </>
        )
      )}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default TransporterUploadPage;